import React from 'react'
import classNames from 'classnames'

import { mq } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import { Text } from 'marketing-site/src/library/elements/Text'
import { RichText } from 'marketing-site/src/mvp/library/elements/RichText'

const getSpacing = (titleSize: IProps['titleSize']) => {
  switch (titleSize) {
    case 'heading-1--mega':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '2rem',
        },
      }
    case 'heading-1':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '2rem',
        },
      }
    case 'heading-1--small':
      return {
        mobile: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '1.5rem',
        },
      }
    case 'heading-2':
    default:
      return {
        mobile: {
          eyebrowTitle: '1rem',
          titleSubhead: '1.5rem',
        },
        tablet: {
          eyebrowTitle: '1.5rem',
          titleSubhead: '1.5rem',
        },
      }
    case 'heading-3':
      return {
        mobile: {
          eyebrowTitle: '1rem',
          titleSubhead: '1rem',
        },
        tablet: {
          eyebrowTitle: '1.25rem',
          titleSubhead: '1rem',
        },
      }
  }
}

export const HeadingBase: React.FC<IProps> = ({
  width = 'default',
  alignment = 'center',
  icon,
  eyebrow,
  title,
  titleSize = 'heading-2',
  subhead,
  listStyle,
  ctas,
  brand,
}) => {
  const classes = classNames('heading-base', {
    [`heading-base--align-${alignment}`]: alignment,
    [`heading-base--width-${width}`]: width,
  })

  return (
    <>
      <div className={classes} data-testid="heading-base">
        {icon && <div className="heading-base__icon">{icon}</div>}
        {eyebrow && (
          <div className="heading-base__eyebrow">
            <Text brand={brand} size="eyebrow">
              {eyebrow}
            </Text>
          </div>
        )}
        {title && (
          <h2 className="heading-base__title">
            <Text brand={brand} size={titleSize}>
              {title}
            </Text>
          </h2>
        )}
        {subhead && (
          <div className="heading-base__subhead">
            <Text brand={brand} size={titleSize === 'heading-3' ? 'subhead-sm' : 'subhead-lg'}>
              {typeof subhead === 'string' ? (
                subhead
              ) : (
                <RichText document={subhead} listStyle={listStyle} />
              )}
            </Text>
          </div>
        )}
        {ctas && <div className="heading-base__ctas">{ctas}</div>}
      </div>

      <style jsx>
        {`
          .heading-base {
            grid-column: 1/-1;
            max-width: 77.5rem;
            margin: 0 auto;

            &__icon {
              display: flex;
              margin-bottom: 1.25rem;

              :global(picture),
              :global(img) {
                height: 2.5rem;
              }
            }

            &__eyebrow + &__title {
              margin-top: ${getSpacing(titleSize).mobile.eyebrowTitle};
            }

            &__title + &__subhead {
              margin-top: ${getSpacing(titleSize).mobile.titleSubhead};
            }

            * + &__ctas {
              margin-top: 2rem;
            }

            &__ctas {
              display: flex;
              flex-direction: column;

              & > :global(.cta-link + .cta-link) {
                margin-top: 0.5rem;
              }

              & > :global(.cta-link + [class*='cta-link--link-only']) {
                margin-top: 1.375rem;
              }
            }

            &--align-start {
              text-align: left;

              .heading-base__icon {
                justify-content: start;
              }
            }

            &--align-center {
              text-align: center;

              .heading-base__icon {
                justify-content: center;
              }
            }
          }

          @media (${mq.tablet}) {
            .heading-base {
              grid-column: 2/-2;

              &__icon {
                margin-bottom: 1.5rem;

                :global(picture),
                :global(img) {
                  height: 3rem;
                }
              }

              &__eyebrow + &__title {
                margin-top: ${getSpacing(titleSize).tablet.eyebrowTitle};
              }

              &__title + &__subhead {
                margin-top: ${getSpacing(titleSize).tablet.titleSubhead};
              }

              * + &__ctas {
                margin-top: 2.5rem;
              }

              &__ctas {
                flex-direction: row;
                align-items: center;

                & > :global(.cta-link + .cta-link) {
                  margin-top: 0rem;
                  margin-left: 1.5rem;
                }
              }

              &--align-start {
                text-align: left;

                .heading-base__ctas {
                  justify-content: start;
                }
              }

              &--align-center {
                text-align: center;

                .heading-base__ctas {
                  justify-content: center;
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .heading-base {
              &--width-narrow {
                grid-column: 4/-4;
              }

              &--width-default {
                grid-column: 3/-3;
              }

              &--width-wide {
                grid-column: 2/-2;
              }
            }
          }

          @media (${mq.desktop}) {
            .heading-base {
              &--width-narrow {
                grid-column: 4/-4;
              }

              &--width-default {
                grid-column: 3/-3;
              }

              &--width-wide {
                grid-column: 2/-2;
              }
            }
          }
        `}
      </style>
    </>
  )
}
